import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, CheckCircle2, ArrowRight, Sparkles, ExternalLink, Lightbulb, Mail, ShieldCheck } from "lucide-react";
import { chapters } from "../data/chapters";
import { useAuth } from "../contexts/AuthContext";

const step1 = chapters.find((c) => c.id === 1);
const CHECKLIST_PATH = "/texas-llc-startup-checklist.pdf";

export default function ChecklistThanksPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const email = state?.email;
  const downloadUrl = state?.download_url || `${window.location.origin}${CHECKLIST_PATH}`;
  const startHref = user ? "/ebook" : "/login?mode=register";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-[var(--c-bg)]" data-testid="checklist-thanks-page">
      <header className="border-b-2 border-[var(--c-border)] bg-[var(--c-bg-95)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="thanks-home-link">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border-2 border-[var(--c-border)] bg-white shadow-[3px_3px_0px_var(--c-shadow)]">
              <img src="/logo-mark.png" alt="Texas LLC Guide" className="h-8 w-8 object-contain" />
            </div>
            <span className="font-heading text-lg font-black text-[var(--c-text)]">Texas <span className="text-[#C8102E]">LLC</span> Guide</span>
          </button>
          <button onClick={() => navigate(user ? "/quickstart" : "/login")} className="rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)]">
            {user ? "My Guide" : "Sign In"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        {/* Confirmation */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="grid items-center gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#A3E6D5] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1A1918]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Checklist sent
            </p>
            <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-[var(--c-text)] sm:text-5xl">
              Your checklist is on its way.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--c-muted)]">
              {email ? (
                <>We&apos;ve emailed it to <strong className="text-[var(--c-text)]" data-testid="thanks-email">{email}</strong>. </>
              ) : null}
              Don&apos;t wait on your inbox — grab the PDF right here, then take a look at what Step 1 actually involves.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={downloadUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                data-testid="thanks-download-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3 text-sm font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
              >
                <Download className="h-4 w-4" /> Download the checklist (PDF)
              </a>
              <span className="inline-flex items-center gap-1.5 text-xs text-[var(--c-muted)]">
                <Mail className="h-3.5 w-3.5" /> Check spam if it hasn&apos;t arrived in a few minutes.
              </span>
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-xs">
            <div className="absolute -bottom-3 -right-3 h-full w-full rounded-2xl bg-[#C8102E]" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-white shadow-[6px_6px_0px_var(--c-shadow)]">
              <div className="bg-[#1E3A8A] px-5 py-4 text-white">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200">Free checklist</p>
                <p className="font-heading text-lg font-black leading-tight">Texas LLC Startup Checklist</p>
              </div>
              <ul className="space-y-2 px-5 py-4 text-sm text-[#1A1918]">
                {["Prerequisites — address & phone", "Steps 1–9, in order", "43 checkboxes", "Official fees & agencies"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#1E3A8A]" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* Step 1 preview */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          data-testid="thanks-step1-preview"
          className="mt-16 rounded-3xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-[6px_6px_0px_var(--c-shadow)] sm:p-10"
        >
          <p className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">
            <Sparkles className="h-3.5 w-3.5" /> Sneak peek — Step 1 (free)
          </p>
          <h2 className="mt-4 font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">{step1.title}</h2>
          <p className="mt-1 text-base text-[var(--c-muted)]">{step1.subtitle}</p>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-[var(--c-text)]">{step1.content.intro}</p>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="font-heading text-lg font-bold text-[var(--c-text)]">Your Step 1 checklist</h3>
              <ul className="mt-3 space-y-2">
                {step1.checklistItems.map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 text-sm text-[var(--c-text)]">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 border-[var(--c-border)] bg-white" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              {step1.content.sections.slice(0, 1).map((sec) => (
                <div key={sec.heading}>
                  <h3 className="font-heading text-lg font-bold text-[var(--c-text)]">{sec.heading}</h3>
                  <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-[var(--c-muted)]">
                    {sec.items.slice(0, 4).map((it) => <li key={it}>{it}</li>)}
                  </ul>
                </div>
              ))}
              <div className="rounded-xl border-2 border-dashed border-[#1E3A8A]/50 bg-[#1E3A8A]/5 p-4">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#1E3A8A]"><Lightbulb className="h-4 w-4" /> Pro tip</p>
                <p className="mt-1 text-sm text-[var(--c-text)]">{step1.content.proTip}</p>
              </div>
              {step1.links?.[0] && (
                <a href={step1.links[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] underline decoration-2 underline-offset-2">
                  {step1.links[0].label} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>

          <div className="mt-10 flex flex-col items-start gap-4 border-t-2 border-[var(--c-border)] pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-heading text-xl font-black text-[var(--c-text)]">Ready to check these off for real?</p>
              <p className="text-sm text-[var(--c-muted)]">Steps 0–2 are free with an account — interactive checklist, cost estimator and saved progress included.</p>
            </div>
            <button
              data-testid="thanks-start-free-button"
              onClick={() => navigate(startHref)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-[var(--c-border)] bg-[#C8102E] px-6 py-3 text-sm font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
            >
              {user ? "Open the guide" : "Start free — no card needed"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </motion.section>

        <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-[var(--c-muted)]">
          <ShieldCheck className="h-3.5 w-3.5" /> The Full Guide ($19, one-time) unlocks Steps 3–9 with a 30-day money-back guarantee.
        </p>
      </main>
    </div>
  );
}
